import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { HttpStatusCode, PejHttpError } from '@common-lib';

import { useOptionalUser, useRoot } from '@frontend/utils';

import { makePlacesQueryOptions } from './use-places-query';

export interface PlacesQueriesMapItem {
  siteUnitId: string;
  shopId: string;
  enabled: boolean;
}

export const usePlacesQueriesMap = (
  items: PlacesQueriesMapItem[],
): Map<string, boolean> => {
  const { removeLocalSession } = useRoot();
  const { user } = useOptionalUser();

  const results = useQueries({
    queries: items.map((item) => ({
      ...makePlacesQueryOptions(item.shopId, user?.locale),
      enabled: item.enabled,
    })),
  });

  const hasAuthError = results.some(
    (r) =>
      r.isError &&
      r.error instanceof PejHttpError &&
      (r.error.statusCode === HttpStatusCode.UNAUTHORIZED ||
        r.error.statusCode === HttpStatusCode.LOCKED),
  );

  useEffect(() => {
    if (hasAuthError) {
      removeLocalSession();
    }
  }, [hasAuthError, removeLocalSession]);

  return useMemo(() => {
    const map = new Map<string, boolean>();
    items.forEach((item, i) => {
      if (item.shopId) {
        const result = results[i];
        map.set(
          item.shopId,
          item.enabled &&
            !result?.isPending &&
            !result?.isError &&
            (result?.data?.length ?? 0) > 0,
        );
      }
    });
    return map;
  }, [items, results]);
};
